import { z } from 'zod';
import { emailSchema } from './signUp.js';
/**
 * Who is in an organisation, and who has been asked to join.
 *
 * Members and invitations travel together because they answer one question —
 * who can get in — and a screen showing only accepted members would let an
 * admin forget a working link sitting in the inbox of somebody who left.
 *
 * Four roles. `owner` runs the organisation and is the only role that may act
 * on another owner; `admin` does everything else administrative; `member` does
 * the work, including sending; `viewer` reads and changes nothing.
 *
 * Every one of them is checked — by `may()` in the domain, through the
 * permission guard on each route group. That is the condition this list is
 * allowed to grow under: a role that is declared and not enforced reads as a
 * control while granting everything, which is why there were two for so long.
 */
export const orgRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer']);
export const memberSchema = z.object({
    userId: z.string(),
    /** Empty when they have not given one. The screen falls back to the address. */
    name: z.string(),
    email: z.string(),
    role: orgRoleSchema,
    joinedAt: z.iso.datetime(),
    /** True for the person reading the screen, so it can stop them removing themselves. */
    isYou: z.boolean(),
});
export const invitationSchema = z.object({
    email: z.string(),
    role: orgRoleSchema,
    invitedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const teamSchema = z.object({
    members: z.array(memberSchema),
    invitations: z.array(invitationSchema),
});
export const inviteRequestSchema = z.object({
    email: emailSchema,
    role: orgRoleSchema,
});
export const setRoleRequestSchema = z.object({ role: orgRoleSchema });
/**
 * What the acceptance page knows before anybody types.
 *
 * The address is shown and not asked for: it comes from the invitation, so
 * somebody holding a link cannot create an account at an address of their
 * choosing inside another organisation.
 */
export const invitationPreviewSchema = z.object({
    email: z.string(),
    organizationName: z.string(),
});
/**
 * Every outcome is a 200.
 *
 * A refused acceptance is an answer to a well-formed question rather than a
 * failed request, and a 401 here would make some browsers offer their own
 * basic-auth dialog over the page.
 *
 * There is no request shape: the name and the address come from the identity
 * provider, and the role comes from the invitation. Nothing is left for the
 * page to send, which is the property that stops somebody holding a link from
 * choosing either.
 */
export const acceptInvitationResponseSchema = z.object({
    status: z.enum([
        'joined',
        'unusable',
        'address-taken',
        /** Signed in as somebody other than the person invited. */
        'wrong-address',
    ]),
    /** Who the invitation was for, present only with `wrong-address`. */
    invited: z.string().optional(),
});
export const inviteResponseSchema = z.object({
    status: z.enum([
        'invited',
        'already-a-member',
        'already-invited',
        'address-taken',
        /** The plan has no room for another person. */
        'no-seats',
    ]),
    /** How many the plan allows, present only with `no-seats`. */
    seats: z.number().int().optional(),
});
//# sourceMappingURL=team.js.map